import React, { Suspense, useCallback, useContext, useEffect } from 'react';
import { Route, Routes, Outlet, useLocation } from 'react-router-dom';
import Loading from './components/Loading';
import User from './pages/User';
import { PrivateRoute } from './components/PrivateRoute';
import RegisterForm from './components/RegisterForm';
import LoginForm from './components/LoginForm';
import NotFound from './pages/NotFound';
import Setting from './pages/Setting';
import EditUser from './pages/User/EditUser';
import AddUser from './pages/User/AddUser';
import { API, showError } from './helpers';
import PasswordResetForm from './components/PasswordResetForm';
import GitHubOAuth from './components/GitHubOAuth';
import PasswordResetConfirm from './components/PasswordResetConfirm';
import { UserContext } from './context/User';
import { StatusContext } from './context/Status';
import Layout from './components/Layout';
import AppErrorBoundary from './components/AppErrorBoundary';
import { lazyWithRetry } from './helpers/lazyWithRetry';
import { readStoredUser } from './helpers/utils';
import Dashboard from './pages/Dashboard';
import RoutesPage from './pages/Routes';

const Provider = lazyWithRetry(() => import('./pages/Provider'), {
  key: 'provider-list',
});
const ProviderDetail = lazyWithRetry(
  () => import('./pages/Provider/ProviderDetail'),
  {
    key: 'provider-detail',
  }
);
const Token = lazyWithRetry(() => import('./pages/Token'), {
  key: 'token-page',
});
const Log = lazyWithRetry(() => import('./pages/Log'), {
  key: 'log-page',
});
const RawErrorView = lazyWithRetry(() => import('./pages/Log/RawErrorView'), {
  key: 'raw-error-view',
});
const About = lazyWithRetry(() => import('./pages/About'), {
  key: 'about-page',
});

// Layout wrapper for authenticated routes
const AppLayout = () => {
  const location = useLocation();
  return (
    <Layout>
      <AppErrorBoundary
        title='页面加载失败'
        stage='route_render'
        path={location.pathname}
        resetKeys={[location.pathname]}
      >
        <Suspense fallback={<Loading label='正在加载页面模块...' />}>
          <Outlet />
        </Suspense>
      </AppErrorBoundary>
    </Layout>
  );
};

function App() {
  const [, userDispatch] = useContext(UserContext);
  const [, statusDispatch] = useContext(StatusContext);
  const location = useLocation();

  const loadUser = useCallback(() => {
    const user = readStoredUser();
    if (user) {
      userDispatch({ type: 'login', payload: user });
    }
  }, [userDispatch]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await API.get('/api/status');
      const { success, data } = res.data;
      if (success) {
        localStorage.setItem('status', JSON.stringify(data));
        statusDispatch({ type: 'set', payload: data });
        localStorage.setItem('system_name', data.system_name);
        localStorage.setItem('footer_html', data.footer_html);
        localStorage.setItem('home_page_link', data.home_page_link);
      } else {
        showError('无法正常连接至服务器！');
      }
    } catch (e) {
      showError('无法正常连接至服务器！');
    }
  }, [statusDispatch]);

  useEffect(() => {
    loadUser();
    loadStatus();
  }, [loadStatus, loadUser]);

  return (
    <AppErrorBoundary
      title='控制台初始化失败'
      stage='app_bootstrap'
      path={location.pathname}
      resetKeys={[location.pathname]}
    >
      <Routes>
        {/* Public Routes */}
        <Route path='/login' element={<LoginForm />} />
        <Route path='/register' element={<RegisterForm />} />
        <Route path='/reset' element={<PasswordResetForm />} />
        <Route path='/user/reset' element={<PasswordResetConfirm />} />
        <Route path='/oauth/github' element={<GitHubOAuth />} />
        <Route
          path='/log/raw'
          element={
            <PrivateRoute>
              <Suspense fallback={<Loading label='正在加载日志详情...' />}>
                <RawErrorView />
              </Suspense>
            </PrivateRoute>
          }
        />

        {/* Authenticated Routes with Layout */}
        <Route
          element={
            <PrivateRoute>
              <AppLayout />
            </PrivateRoute>
          }
        >
          <Route path='/' element={<Dashboard />} />
          <Route path='/provider' element={<Provider />} />
          <Route path='/provider/:id' element={<ProviderDetail />} />
          <Route path='/token' element={<Token />} />
          <Route path='/routes' element={<RoutesPage />} />
          <Route path='/log' element={<Log />} />
          <Route path='/user' element={<User />} />
          <Route path='/user/edit/:id' element={<EditUser />} />
          <Route path='/user/edit' element={<EditUser />} />
          <Route path='/user/add' element={<AddUser />} />
          <Route path='/setting' element={<Setting />} />
          <Route path='/about' element={<About />} />
        </Route>

        <Route path='*' element={<NotFound />} />
      </Routes>
    </AppErrorBoundary>
  );
}

export default App;
